import { formatClientClockValue } from '../content/applications/quote/shiftTimePresentation';
import { rateTypeDisplayLabel } from 'src/utils/rateTypeIdentity';
/**
 * Utility functions for comparing quote data with lastEventHistory
 */

import { formatNumber } from '../utils/formatters';

/**
 * Normalize date values for comparison
 * Converts ISO dates to YYYY-MM-DD format for consistent comparison
 */
const normalizeValueForComparison = (value: any, field: string): any => {
  // For date fields, extract just the date part (YYYY-MM-DD)
  if ((field === 'startDate' || field === 'endDate') && value) {
    // Handle ISO format: "2026-04-20T00:00:00.000Z"
    if (typeof value === 'string' && value.includes('T')) {
      return value.split('T')[0];
    }
    // Already in YYYY-MM-DD format
    return value;
  }
  return value;
};

export interface ShiftComparison {
  shiftNumber: string;
  status: 'unchanged' | 'modified' | 'deleted' | 'new';
  changes: Record<string, { base: any; requested: any }>;
  originalData?: any; // The original approved data (Base)
  decisionBaseData?: any; // Reviewed values retained by the existing Reject action
  historyData?: any;  // The requested history data (Requested)
}

export interface TopLevelChanges {
  venue?: { base: string; requested: string };
  address?: { base: string; requested: string };
  clientPo?: { base: string; requested: string };
  title?: { base: string; requested: string };
  billTo?: { base: string; requested: string };
}

/**
 * Compare current shifts with lastEventHistory shifts
 * Note: lastEventHistory contains the NEW version, currentShifts is the OLD version
 * Logic:
 * - Shifts in currentShifts but NOT in lastEventHistory = DELETED
 * - Shifts in lastEventHistory but NOT in currentShifts = NEW
 */
export const compareShifts = (
  currentShifts: any[],
  historyShifts: any[] | undefined,
  decisionBaseShifts?: any[]
): ShiftComparison[] => {
  if (!historyShifts || historyShifts.length === 0) {
    return currentShifts.map(shift => ({
      shiftNumber: shift.shiftNumber || '',
      status: 'unchanged',
      changes: {},
      originalData: shift
    }));
  }

  const result: ShiftComparison[] = [];
  const processedHistoryIndices = new Set<number>();

  // Helper to find the best match in history for a given current shift
  const findHistoryMatch = (currentShift: any) => {
    const shiftNum = String(currentShift.shiftNumber || '').trim();
    const currentId = currentShift.id;

    // 1. Priority: Match by Numeric ID
    if (typeof currentId === 'number') {
      const idMatch = historyShifts.findIndex((sh, i) => !processedHistoryIndices.has(i) && (sh.shiftId === currentId || (sh.shiftId == null && sh.id === currentId)));
      if (idMatch !== -1) return idMatch;
    }

    // 2. Secondary: Match by shiftNumber
    if (shiftNum && !shiftNum.startsWith('new-hist-') && !String(shiftNum).startsWith('dup-')) {
      const numMatch = historyShifts.findIndex((sh, i) => !processedHistoryIndices.has(i) && String(sh.shiftNumber || '').trim() === shiftNum);
      if (numMatch !== -1) return numMatch;
    }

    // 3. Tertiary: Fuzzy Match by content
    const fuzzyMatch = historyShifts.findIndex((sh, i) => {
      if (processedHistoryIndices.has(i)) return false;

      const shDate = normalizeValueForComparison(sh.startDate, 'startDate');
      const curDate = normalizeValueForComparison(currentShift.startDate, 'startDate');

      return (
        shDate === curDate &&
        sh.position === currentShift.position &&
        sh.quantity === currentShift.quantity &&
        sh.startTime === currentShift.startTime &&
        sh.endTime === currentShift.endTime
      );
    });

    return fuzzyMatch;
  };

  // Process current shifts (Base version)
  for (const currentShift of currentShifts) {
    const matchIndex = findHistoryMatch(currentShift);

    if (matchIndex === -1) {
      // No match in history -> shift was removed in the new version
      result.push({
        shiftNumber: currentShift.shiftNumber || '',
        status: 'deleted',
        changes: {},
        originalData: currentShift
      });
    } else {
      // Match found
      processedHistoryIndices.add(matchIndex);
      const historyShift = historyShifts[matchIndex];
      const changes: Record<string, { base: any; requested: any }> = {};
      // Client quote fields only: worker rates and staffing state are never revisions.
      const fieldsToCompare = [
        'startDate', 'position', 'quantity', 'startTime', 'endTime',
        'notes', 'rateType', 'callType', 'mealBreak', 'rate',
        'registerHours', 'overTimeHours', 'DoubleTimeHours', 'addOns', 'miscFees', 'area', 'toolsGear'
      ];

      for (const field of fieldsToCompare) {
        const baseVal = normalizeValueForComparison(currentShift[field], field);
        const reqVal = normalizeValueForComparison(historyShift[field], field);

        if (baseVal !== reqVal && !(baseVal == null && reqVal == null)) {
          changes[field] = {
            base: currentShift[field],
            requested: historyShift[field]
          };
        }
      }

      let status: 'unchanged' | 'modified' | 'deleted' | 'new' = Object.keys(changes).length > 0 ? 'modified' : 'unchanged';

      const shiftNum = String(historyShift.shiftNumber || currentShift.shiftNumber || '').trim();

      result.push({
        shiftNumber: shiftNum,
        status,
        changes,
        originalData: currentShift,
        ...(decisionBaseShifts ? { decisionBaseData: decisionBaseShifts.find(row => String(row.id) === String(currentShift.id)) } : {}),
        historyData: historyShift
      });
    }
  }

  // Any remaining history shifts are NEW additions from the history version
  historyShifts.forEach((hShift, index) => {
    if (!processedHistoryIndices.has(index)) {
      result.push({
        shiftNumber: hShift.shiftNumber || `new-hist-${index}`,
        status: 'new',
        changes: {},
        historyData: hShift
      });
    }
  });

  return result;
};

/**
 * Compare top-level quote fields
 */
export const compareTopLevel = (
  currentData: any,
  historyData: any
): TopLevelChanges => {
  const changes: TopLevelChanges = {};
  const fieldsToCompare: (keyof TopLevelChanges)[] = ['venue', 'address', 'clientPo', 'title', 'billTo'];

  for (const field of fieldsToCompare) {
    if (currentData[field] !== historyData?.[field]) {
      changes[field] = {
        requested: historyData?.[field] || 'N/A',
        base: currentData[field] || 'N/A'
      };
    }
  }

  return Object.keys(changes).length > 0 ? changes : {};
};

/**
 * Get field display name
 */
export const getFieldDisplayName = (field: string): string => {
  const displayNames: Record<string, string> = {
    startDate: 'Call Date',
    endDate: 'End Date',
    position: 'Position',
    quantity: 'Quantity',
    startTime: 'Start Time',
    endTime: 'End Time',
    callTime: 'Call Time',
    notes: 'Notes',
    rateType: 'Rate Type',
    callType: 'Call Type',
    mealBreak: 'Meal Break',
    rate: 'Rate',
    desiredRate: 'Desired Rate',
    registerHours: 'ST Hours',
    overTimeHours: 'OT Hours',
    DoubleTimeHours: 'DT Hours',
    addOns: 'Per Diem',
    miscFees: 'Misc Fees',
    totals: 'Total',
    venue: 'Venue',
    address: 'Event Location',
    clientPo: 'Client PO',
    title: 'Job #',
    billTo: 'Bill To'
  };

  return displayNames[field] || field;
};

/**
 * Format value for display
 */
export const formatValueForDisplay = (value: any, field: string): string => {
  if (value === null || value === undefined) return 'N/A';
  if (field === 'rateType') return rateTypeDisplayLabel(value);

  if (field.includes('Date')) {
    return new Date(value).toLocaleDateString();
  }

  if (field === 'rate' || field === 'desiredRate' || field === 'addOns' || field === 'miscFees' || field === 'totals') {
    // If it's already a formatted string with $, return as is
    if (typeof value === 'string' && value.startsWith('$')) return value;
    return `$${formatNumber(value)}`;
  }


  if (field === 'startTime' || field === 'endTime') {
    return formatClientClockValue(String(value));
  }

  return String(value);
};

/** New requests retain their original client snapshot, independent of later Admin edits. */
export const clientRequestOriginalQuote = (quote: any, history: any) => {
  try {
    const metadata = JSON.parse(history?.adminNotes || '{}');
    const issued = metadata.clientRequestIssuedQuote;
    if (issued && Array.isArray(issued.shifts)) return issued;
    if (metadata.clientRequestOriginalUnavailable || metadata.clientRequestDecisionVersion === 2) return { ...history, shifts: history.shiftsHistory || [] };
    const original = metadata.clientRequestOriginalQuote;
    if (original && Array.isArray(original.shifts)) return original;
  } catch { /* Legacy request notes may not contain JSON. */ }
  return quote;
};

/** Transaction/rejection baseline stays separate from the issued comparison. */
export const clientRequestReviewedQuote = (quote: any, history: any) => {
  try {
    const original = JSON.parse(history?.adminNotes || '{}').clientRequestOriginalQuote;
    if (original && Array.isArray(original.shifts)) return original;
  } catch { /* Legacy request notes may not contain JSON. */ }
  return quote;
};
